import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { JobState, JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { createContext, useCallback, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import SendTextDialog from '~components/Job/SendTextDialog';
import { trackJobAssignmentCancelled } from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { Job } from '~hooks/useJob/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobDialog from '../JobDialog';

type JobDialogsContextType = {
  duplicateJob: (job: Job) => void;
  openCancelJobDialog: (job: Job) => void;
  openEditJobFormDialog: (job: Job) => void;
  openSendTextDialog: (job: Job) => void;
};

const JobDialogsContext = createContext<JobDialogsContextType>({
  duplicateJob: noop,
  openCancelJobDialog: noop,
  openEditJobFormDialog: noop,
  openSendTextDialog: noop,
});

enum JobActionDialog {
  CANCEL_JOB = 'cancel_job',
  FORM = 'form',
  TEXT_DRIVER = 'text_driver',
}

type JobActionDialogState = {
  dialog?: JobActionDialog;
  isOpen: boolean;
  job?: Job;
};

function JobDialogsContextProvider({ children }: React.PropsWithChildren) {
  const [jobActionDialog, setJobActionDialog] = useState<JobActionDialogState>({
    isOpen: false,
  });
  const [isCancellingJob, setIsCancellingJob] = useState(false);
  const { driverSchedulerStore, toasterStore, userStore } = useStores();
  const { cancelJob, duplicateJob } = useOrdersDispatch();

  const companyId = getEffectiveUserCompanyId(userStore);

  const closeCurrentDialog = useCallback(() => {
    setJobActionDialog((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCancelJob = useCallback(async () => {
    const jobId = jobActionDialog.job?.id;

    if (!jobId) {
      return;
    }

    try {
      setIsCancellingJob(true);
      const cancelledJob = await cancelJob(jobId, companyId);
      trackJobAssignmentCancelled({
        action: JobStateEvent.CANCEL,
        jobIds: [jobId],
        source: 'scheduler_card',
      });

      driverSchedulerStore.removeUnassignedJob(cancelledJob);
      toasterStore.push(
        alert(
          t('dispatch.job.updated', { name: cancelledJob.jobId }),
          AlertTypes.success,
        ),
      );
      closeCurrentDialog();
    } catch {
      // Error is handled by the hook
    } finally {
      setIsCancellingJob(false);
    }
  }, [closeCurrentDialog, jobActionDialog.job?.jobId]);

  const handleDuplicateJob = useCallback(async (job: Job) => {
    try {
      const clonedJob = await duplicateJob(job.id);

      if (job.status === JobState.REJECTED) {
        await cancelJob(job.id, companyId);
      }

      driverSchedulerStore.addUnassignedJob(clonedJob);

      toasterStore.push(
        alert(t('dispatch.job.copied', { name: clonedJob.jobId }), AlertTypes.success),
      );

      return clonedJob;
    } catch {
      // Error is handled by the hook
    }
  }, []);

  const openCancelJobDialog = useCallback((job: Job) => {
    setJobActionDialog({ job, isOpen: true, dialog: JobActionDialog.CANCEL_JOB });
  }, []);

  const openEditJobFormDialog = useCallback((job: Job) => {
    setJobActionDialog({ job, isOpen: true, dialog: JobActionDialog.FORM });
  }, []);

  const openSendTextDialog = useCallback((job: Job) => {
    setJobActionDialog({ job, isOpen: true, dialog: JobActionDialog.TEXT_DRIVER });
  }, []);

  return (
    <JobDialogsContext.Provider
      value={{
        duplicateJob: handleDuplicateJob,
        openCancelJobDialog,
        openEditJobFormDialog,
        openSendTextDialog,
      }}
    >
      {children}
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui="page-dispatch__job-dialogs-context__button"
      >
        <JobDialog
          isOpen={
            jobActionDialog.dialog === JobActionDialog.FORM && jobActionDialog.isOpen
          }
          job={jobActionDialog.job}
          onClose={(updatedJob) => {
            closeCurrentDialog();

            if (updatedJob) {
              driverSchedulerStore.updateJob(updatedJob);
            }
          }}
        />

        <ConfirmationDialog
          isBusy={isCancellingJob}
          isOpen={
            jobActionDialog.dialog === JobActionDialog.CANCEL_JOB &&
            jobActionDialog.isOpen
          }
          onClose={closeCurrentDialog}
          onConfirm={handleCancelJob}
          title={t('dispatch.job.cancel_job')}
        >
          <Typography>{t('dispatch.job.cancel_description')}</Typography>
        </ConfirmationDialog>

        <SendTextDialog
          isOpen={
            jobActionDialog.dialog === JobActionDialog.TEXT_DRIVER &&
            jobActionDialog.isOpen
          }
          mode="job_driver"
          source={jobActionDialog.job}
          onClose={closeCurrentDialog}
        />
      </Box>
    </JobDialogsContext.Provider>
  );
}

export { JobDialogsContextProvider };
export default JobDialogsContext;
